(ns dre.components.worldmap
  (:require
   [applied-science.js-interop :as j]
   [clj-commons.humanize :refer [intcomma]]
   ["chroma-js" :as chroma]
   ["datamaps/dist/datamaps.world.min.js" :as Datamap]
   ["d3" :as d3]
   [dre.components.icon :refer [icon]]
   [dre.i18n.views :refer [tr]]
   [dre.page-util :refer [around]]
   [dre.utils.bem :as bem]
   [reagent.core :as r]
   [goog.dom :as gdom]
   ["react-sizeme" :as react-sizeme]))

(def component-name :worldmap)

;; Public API

(defn scale
  [colors]
  (let [f (-> (j/call chroma :scale (clj->js colors))
              (j/call :correctLightness))]
    (fn [n]
      (f n))))

(defn palette
  "Takes a map of keywords to counts, and generates a new color palette"
  [values color-range]
  (let [svs (sort values)
        rsvs (reverse svs)
        ll (first svs)  ;; Lower limit
        ul (first rsvs) ;; Upper limit

        colors (scale color-range)

        scale (.scaleLinear d3)]
    (-> scale
        (.range (into-array [0 1]))
        (.domain (into-array [ll ul])))
    (fn [num]
      (colors (scale num)))))

(defonce zoom-state (atom ""))

(defn set-initial-zoom
  [datamap]
  (-> (j/get datamap :svg)
      (.selectAll "g")
      (.attr "transform" @zoom-state))
  datamap)

(defn add-zoom
  [el zoom-state datamap]
  (let [svg (j/call (j/call d3 :select el) :select "svg")
        max-zoom 5
        [w h] [(.. svg (attr "width"))
               (.. svg (attr "height"))]
        bound
        (fn [[scale x y]]
          {:translate
           (into-array
            [(min (* (/ w h) (- scale 1))
                  (max (* w (- 1 scale)) x))
             (min 0 (max (* h (- 1 scale)) y))])

           :scale scale})
        redraw
        (fn [event d]
          (let [t [(j/get (j/get event :transform) :k)
                   (j/get (j/get event :transform) :x)
                   (j/get (j/get event :transform) :y)]
                t* (bound t)
                transform (str
                           "translate(" (:translate t*) ")"
                           "scale(" (:scale t*) ")")]
            (reset! zoom-state transform)
            (-> svg
                (.selectAll "g")
                (.attr "transform" transform))))
        zoom
        (.. (j/call d3 :zoom)
            (scaleExtent (into-array [1 max-zoom]))
            (on "zoom" redraw))]
    
    (j/call svg :call zoom)
    datamap))

(defn worldmap*
  [opts width height _allow-zoom?]
  (let [tpl
        (fn [geo data]
          (str "<div class=\"hoverinfo\"><strong>"
               (j/get (j/get geo :properties) :name)
               "</strong> &mdash; "
               (or (if (:dont-round-numbers? opts)
                     (intcomma (j/get data :count))
                     (around (j/get data :count))) "unknown")
               "</div>"))
        v (map :val (vals (:country-counts opts)))
        color-scale-active (palette v ["#dcdcdc" "#d046ff"])
        color-scale-inactive (palette v ["#dcdcdc" "#aaa"])
        rf
        (fn [mem [k {:keys [val class] :as v}]]
          (assoc mem k {:count val
                        :fillColor
                        (if (or (empty? class) (some #{"active"} class))
                          (color-scale-active val)
                          (color-scale-inactive val))}))
        element (atom nil)
        destroy!
        (fn [_this]
          (when @element
            (gdom/removeChildren @element)))
        repaint!
        (fn [this opts width height allow-zoom?]
          (destroy! this)
          (Datamap.
           (clj->js {:element @element
                     :projection "equirectangular"
                     :width (min width (* 2 height))
                     :height height
                     :data (reduce rf {} (:country-counts opts))
                     :done (comp
                            (:done opts)
                            set-initial-zoom
                            (if @allow-zoom?
                              (partial add-zoom @element zoom-state)
                              identity))
                     :fills {:defaultFill "#DCDCDC"}
                     :geographyConfig
                     {:borderColor "#F9F9F9"
                      :highlightFillColor "#d046ff"
                      :hideAntarctica false
                      :popupTemplate tpl}})))]
    (r/create-class
     {:component-did-mount
      (fn [this]
        (repaint! this opts width height _allow-zoom?))
      :component-will-unmount
      (fn [this]
        (destroy! this))
      :component-will-update
      (fn [this [_ opts width height allow-zoom?]]
        (repaint! this opts width height allow-zoom?))
      :reagent-render
      (fn [opts width height allow-zoom?]
        [:div
         {:style
          {:width "100%"
           :height "100%"}}
         [:div
          {:ref #(reset! element %)
           :class [(bem/bm component-name :zoomable)]
           :style
           {
            :width (min width (* 2 height))
            :height height}}]
         [:button
          {:class [(bem/bm :button :solid) (bem/bm :button :small) :worldmap-zoom-button]
           :on-click #(reset! allow-zoom? (not @allow-zoom?))}
          [icon :common :search]
          (if @allow-zoom?
            [tr "Disable zoom"]
            [tr "Enable zoom"])]])})))

(defn worldmap
  [_opts]
  (let [allow-zoom? (r/atom false)]
    (fn [opts]
      [:div {:class component-name}
       [(r/adapt-react-class react-sizeme/SizeMe)
        {:monitorWidth true :monitorHeight true}
        (fn [props]
          (let [props (js->clj props :keywordize-keys true)]
            (r/as-element [worldmap*
                           opts
                           (:width (:size props))
                           (:height (:size props))
                           allow-zoom?])))]])))
